import UniDataTable from '@/common/classes/UniDataTable'
import { formatMenuCountString } from '@/common/utils/formatMenuCountString'
import { getPrimeIcon, iconMapper } from '@/common/utils/iconMapper'
import {
  hasPlatformIcon,
  resolvePlatformIcon,
  type PlatformIconResolution,
} from '@/common/utils/resolvePlatformIcon'
import { parseService } from '@/services'
import { useAppStore } from '@/stores/app'
import { useMenuCountersStore } from '@/stores/menuCounters'
import { usePlatformConnectionStore } from '@/stores/platformConnection'
import { useUserStore } from '@/stores/user'
import { computed, ref } from 'vue'

export type MenuItemIconResolution =
  | { kind: 'image'; url: string }
  | { kind: 'prime'; primeClass: string }
  | { kind: 'none' }

// Глобальный флаг для предотвращения множественной загрузки меню
const menuLoadingPromise = ref<Promise<void> | null>(null)
// Отдельный флаг для загрузки меню (не связан с загрузкой страниц)
const isMenuLoading = ref<boolean>(false)

export function useMenu() {
  const appStore = useAppStore()
  const userStore = useUserStore()
  const menuCountersStore = useMenuCountersStore()

  // Computed свойства для доступа к меню из store
  const sideMenu = computed(() => appStore.sideMenu)
  const topMenu = computed(() => appStore.topMenu)
  const rawMenu = computed(() => appStore.rawMenu)
  // Используем отдельный флаг для загрузки меню
  const isLoading = computed(() => isMenuLoading.value)

  // Проверяем, загружено ли меню
  const hasMenu = computed(() => sideMenu.value.length > 0)

  // Загрузка конфига (включая меню)
  const loadMenu = async (): Promise<void> => {
    if (usePlatformConnectionStore().isDisconnected) return

    if (!hasMenu.value) {
      // Если уже идет загрузка - ждем ее завершения
      if (menuLoadingPromise.value) return menuLoadingPromise.value

      isMenuLoading.value = true
      menuLoadingPromise.value = appStore.getConfig()

      try {
        await menuLoadingPromise.value
      } finally {
        isMenuLoading.value = false
        menuLoadingPromise.value = null
      }
    }
  }

  const getItemIcon = (item: any): string => {
    if (!item.icon || item.icon === '') {
      if(hasChildren(item))
        return 'pi pi-folder'
      else
        return 'pi pi-file'
    }

    return getPrimeIcon(item.icon)
  }

  const hasChildren = (item: any): boolean => {
    return item.children && item.children.length > 0
  }

  // Получение пути для навигации
  const getItemPath = (item: any): any => {
    if (item.path) {
      return item.path
    } else if (item.branch_id) {
      return {
        name: 'platform',
        params: { id: item.branch_id.toString() }
      }
    }
    return null
  }

  const getItemCounts = (item: any): number[] | undefined => {
    const itemId = item?.id || item?.branch_id
    if (itemId) {
      const fromStore = menuCountersStore.getCounters(itemId)
      if (fromStore) return fromStore
    }
    return item?.count
  }

  const resolveMenuItemIcon = (item: any): MenuItemIconResolution => {
    const icon = String(item?.icon ?? '').trim()
    if (!icon) return { kind: 'none' }

    const resolution: PlatformIconResolution = resolvePlatformIcon(icon)
    if (resolution.kind === 'image') {
      return { kind: 'image', url: resolution.url }
    }
    // Explicit map may equal default (e.g. question → pi-question-circle) — still show it.
    if (hasPlatformIcon(icon) || resolution.primeClass !== iconMapper.default) {
      return { kind: 'prime', primeClass: resolution.primeClass }
    }
    return { kind: 'none' }
  }

  const getItemCounter = (item: any): number | undefined => {
    if (hasChildren(item)) return undefined

    const itemId = item.id || item.branch_id
    if (!itemId) return undefined

    const counter = menuCountersStore.getCounter(itemId) ?? item?.count?.[0] ?? 0
    return counter && counter > 0 ? counter : undefined
  }

  const formatToolbarLabel = (item: any): string => {
    const name = formatItemName(item)
    const countString = formatMenuCountString(getItemCounts(item))
    return countString ? `${name} ${countString}` : name
  }

  /** Get fio (full name) from chain data, same source as getCurrentChainData().getFieldAsString("fio") */
  const getFioFromChainData = (chainData: any): string => {
    if (!chainData) return ''
    const raw =
      chainData.fields?.fields?.fio?.value ??
      chainData.fields?.fio?.value ??
      (typeof chainData.fio === 'string' ? chainData.fio : chainData.fio?.value)
    return raw != null ? String(raw) : ''
  }

  /** Build context for Velocity-style templates */
  const buildVelocityContext = (chainData: any): Record<string, any> => {
    const ctx: Record<string, any> = {
      platformId: appStore.currentPlatform || '',
      fio: getFioFromChainData(chainData) || userStore.userFullName || '',
      username: userStore.user?.username || '',
    }
    if (!chainData) return ctx
    const positionsRaw =
      chainData.fields?.fields?.positions?.value ??
      chainData.fields?.positions?.value ??
      chainData.positions?.value ??
      chainData.positions
    if (positionsRaw != null) {
      const rows = Array.isArray(positionsRaw) ? positionsRaw : positionsRaw?.rows ?? []
      ctx.positions = new UniDataTable(Array.isArray(rows) ? { rows } : rows)
    }
    return ctx
  }

  const isVelocityTemplate = (name: string): boolean =>
    /#(?:set|foreach|if)\s*\(/.test(name) ||
    /\$positions\b/.test(name) ||
    /\.getRow\s*\(/.test(name) ||
    /\.getFieldAsString\s*\(/.test(name) ||
    /\.getFieldAsLong\s*\(/.test(name)

  const formatItemName = (item: any): string => {
    const name = (item?.name ?? '').trim()
    if (!name) return name
    if (!name.includes('$')) return name

    const chainData = appStore.sessionChainData
    const isVelocity = isVelocityTemplate(name)
    if (isVelocity && chainData != null) {
      try {
        const context = buildVelocityContext(chainData)
        let result = parseService.velocityFormat(name, context) || name
        if (result.startsWith(')')) result = result.slice(1).trim()
        return result
      } catch (e) {
        console.warn('Velocity format failed for menu name:', e)
      }
    }

    const fioFromChain = chainData ? getFioFromChainData(chainData) : ''
    const context: Record<string, string> = {
      platformId: appStore.currentPlatform || item?.platform || '',
      fio: fioFromChain || userStore.userFullName || '',
      username: userStore.user?.username || '',
    }
    const resolver = (varName: string) => {
      const key = varName.trim()
      let val = context[key] ?? context[key.toLowerCase()]
      if (val !== undefined && val !== null) return val
      if (key.toLowerCase() === 'fio') return fioFromChain || userStore.userFullName || ''
      if (key.toLowerCase() === 'username') return userStore.user?.username ?? ''
      if (key.toLowerCase() === 'platformid') return appStore.currentPlatform ?? item?.platform ?? ''
      return ''
    }
    return parseService.literator(name, resolver)
  }

  return {
    // State
    sideMenu,
    topMenu,
    rawMenu,
    isLoading,

    // Computed
    hasMenu,

    // Actions
    loadMenu,
    getItemIcon,
    hasChildren,
    getItemPath,
    getItemCounts,
    getItemCounter,
    formatItemName,
    formatToolbarLabel,
    resolveMenuItemIcon,
  }
}
